import { useEffect, useState } from 'react'
import { Box, Flex, Spinner, VStack, Grid, Input, Text, toaster, useT } from '@minetrace/ui'
import {
  LuBuilding2 as Building2,
  LuMapPinned as MapPinned,
  LuLandPlot as LandPlot,
} from 'react-icons/lu'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  FormSection,
  FormLabel,
  useFormColors,
} from '@minetrace/ui/forms'
import { tenantsService, componentTypesService } from '../../api/services'
import type { TenantComponentResponse } from '../../api/services/tenants.service'
import type { ComponentTypeItem } from '../../api/models/maestros.models'
import { useRequest } from '../../hooks'
import UbigeoSelect from '../../components/UbigeoSelect'
import PolygonEditor, { type PolygonEditorValue } from '../../components/PolygonEditor'
import ComponentsEditor, { type ComponentEditorItem, type ComponentTypeOption } from '../../components/ComponentsEditor'
import CharCounter from '../../components/CharCounter'
import { TenantFieldLengths } from '../../lib/fieldLengths'
import { getUtmZone, latLngToUtm, utmToLatLng } from '../../utils/utmConverter'

const schema = z.object({
  // Opcional pero con formato: mismo patrón "optional + refine" que ubigeoCode abajo.
  // 11 dígitos, como el resto de RUC del sistema.
  ruc: z.string().optional().refine(
    (val) => !val || /^\d{11}$/.test(val),
    { message: 'miEmpresa.zod.rucFormat' },
  ),
  legalName: z.string().min(1, 'miEmpresa.zod.legalNameRequired'),
  commercialName: z.string().optional(),
  prefix: z.string().max(3, 'miEmpresa.zod.prefixMaxLength').optional(),
  taxAddress: z.string().optional(),
  ubigeoCode: z.string().optional().refine(
    (val) => !val || val.length === 6,
    { message: 'miEmpresa.zod.ubigeoLength' },
  ),
})

type FormValues = z.infer<typeof schema>

export default function MiEmpresaPage() {
  const c = useFormColors()
  const { t } = useT()

  const [loading, setLoading] = useState(true)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [polygonValue, setPolygonValue] = useState<PolygonEditorValue>({
    vertices: [],
    utmZone: 18,
    newNorthing: '',
    newEasting: '',
  })
  const [components, setComponents] = useState<TenantComponentResponse[]>([])
  const [availableTypes, setAvailableTypes] = useState<ComponentTypeOption[]>([])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ruc: '',
      legalName: '',
      commercialName: '',
      prefix: '',
      taxAddress: '',
      ubigeoCode: '',
    },
  })

  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = form
  const ubigeoCode = watch('ubigeoCode')

  const { execute: saveTenant } = useRequest(tenantsService.updateMine, {
    successMessage: t('miEmpresa.toasts.saved'),
  })
  const { execute: addComponentReq } = useRequest(tenantsService.addComponent, {
    successMessage: t('miEmpresa.toasts.componentAdded'),
  })
  const { execute: removeComponentReq } = useRequest(tenantsService.removeComponent, {
    successMessage: t('miEmpresa.toasts.componentRemoved'),
  })

  useEffect(() => {
    let cancelled = false
    componentTypesService
      .getAll({ categoryCode: 'beneficio', pageSize: 200 })
      .then((res) => {
        if (cancelled) return
        const opts: ComponentTypeOption[] = res.items.map((it: ComponentTypeItem) => ({
          value: it.id,
          label: it.name,
          tierCode: it.tierCode,
          tierName: it.tierName,
          description: it.description,
        }))
        setAvailableTypes(opts)
      })
      .catch(() => { /* silent — picker just shows empty */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await tenantsService.getMine()
        if (cancelled) return
        setTenantId(data.id)
        reset({
          ruc: data.ruc ?? '',
          legalName: data.legalName ?? '',
          commercialName: data.commercialName ?? '',
          prefix: data.prefix ?? '',
          taxAddress: data.taxAddress ?? '',
          ubigeoCode: data.ubigeoCode ?? '',
        })
        // Parse polygon (if any) → vertices in UTM
        const zone = data.plantUtmZone ?? getUtmZone(data.ubigeoCode)
        let verts: { northing: string; easting: string }[] = []
        if (data.plantBoundaryGeoJson) {
          try {
            const geo = JSON.parse(data.plantBoundaryGeoJson)
            const coords: number[][] = geo.type === 'Polygon' ? geo.coordinates[0] : []
            const points = coords.length > 1
              && coords[0][0] === coords[coords.length - 1][0]
              && coords[0][1] === coords[coords.length - 1][1]
              ? coords.slice(0, -1)
              : coords
            verts = points.map((p) => {
              const { northing, easting } = latLngToUtm(p[1], p[0], zone)
              return { northing: String(northing), easting: String(easting) }
            })
          } catch {
            verts = []
          }
        }
        setPolygonValue({ vertices: verts, utmZone: zone, newNorthing: '', newEasting: '' })
        setComponents(data.components ?? [])
      } catch {
        if (!cancelled) toaster.error({ description: t('miEmpresa.loadError') })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [reset, t])

  const onSubmit = async (values: FormValues) => {
    const hasVertices = polygonValue.vertices.length > 0
    if (hasVertices && polygonValue.vertices.length < 3) {
      toaster.error({ description: t('miEmpresa.zod.polygonMinVertices') })
      return
    }

    const updated = await saveTenant({
      // Vacío se manda como undefined (no ''): así el backend lo interpreta como
      // "borrar el RUC" en vez de rechazar la cadena vacía por formato.
      ruc: values.ruc || undefined,
      legalName: values.legalName,
      commercialName: values.commercialName || undefined,
      prefix: values.prefix || undefined,
      taxAddress: values.taxAddress || undefined,
      ubigeoCode: values.ubigeoCode || undefined,
      plantUtmZone: polygonValue.utmZone,
      plantBoundaryVertices: hasVertices
        ? polygonValue.vertices.map((v) => ({ northing: parseInt(v.northing), easting: parseInt(v.easting) }))
        : [],
    })
    if (updated) setComponents(updated.components ?? [])
  }

  // Build polygon GeoJSON for ComponentsEditor preview from the live editor vertices
  const polygonGeoJsonForPreview = (() => {
    if (polygonValue.vertices.length < 3) return null
    try {
      const coords: number[][] = polygonValue.vertices.map((v) => {
        const { lat, lng } = utmToLatLng(parseInt(v.northing), parseInt(v.easting), polygonValue.utmZone)
        return [lng, lat]
      })
      if (coords.length > 0) coords.push([coords[0][0], coords[0][1]])
      return JSON.stringify({ type: 'Polygon', coordinates: [coords] })
    } catch {
      return null
    }
  })()

  const editorComponents: ComponentEditorItem[] = components.map((comp) => ({
    id: comp.id,
    componentTypeId: comp.componentTypeId,
    componentTypeName: comp.componentTypeName,
    componentTypeCode: comp.componentTypeCode,
    name: comp.name,
    northing: comp.northing,
    easting: comp.easting,
    isOutsidePolygon: comp.isOutsidePolygon,
  }))

  const handleAddComponent = async (data: { componentTypeId: string; name?: string; northing: number; easting: number }): Promise<ComponentEditorItem> => {
    const result = await addComponentReq(data)
    if (!result) throw new Error('component_add_failed')
    setComponents((prev) => [...prev, result])
    return {
      id: result.id,
      componentTypeId: result.componentTypeId,
      componentTypeName: result.componentTypeName,
      componentTypeCode: result.componentTypeCode,
      name: result.name,
      northing: result.northing,
      easting: result.easting,
      isOutsidePolygon: result.isOutsidePolygon,
    }
  }

  const handleRemoveComponent = async (componentId: string) => {
    await removeComponentReq(componentId)
    setComponents((prev) => prev.filter((c) => c.id !== componentId))
  }

  if (loading) {
    return (
      <Flex minH="60vh" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Flex>
    )
  }

  return (
    <Box maxW="1400px" mx="auto" px={{ base: 4, md: 8 }} py={8}>
      <Box mb={6}>
        <Flex alignItems="center" gap={3}>
          <Box color={c.accent}><Building2 size={28} /></Box>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color={c.text} tid="miEmpresa.title" />
            <Text fontSize="sm" color={c.textMuted} tid="miEmpresa.subtitle" />
          </Box>
        </Flex>
      </Box>

      <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormSection icon={Building2} titleTid="miEmpresa.sections.identidad">
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={5}>
            <Box>
              <FormLabel tid="miEmpresa.fields.ruc" />
              <Input {...register('ruc')} maxLength={TenantFieldLengths.ruc} inputMode="numeric" />
              <Flex justifyContent="space-between" alignItems="center" mt={1}>
                {errors.ruc ? (
                  <Text fontSize="xs" color={c.error}>{t(errors.ruc.message ?? '')}</Text>
                ) : <span />}
                <CharCounter name="ruc" max={TenantFieldLengths.ruc} mt={0} />
              </Flex>
            </Box>
            <Box>
              <FormLabel tid="miEmpresa.fields.legalName" required />
              <Input {...register('legalName')} maxLength={TenantFieldLengths.legalName} />
              <Flex justifyContent="space-between" alignItems="center" mt={1}>
                {errors.legalName ? (
                  <Text fontSize="xs" color={c.error}>{t(errors.legalName.message ?? '')}</Text>
                ) : <span />}
                <CharCounter name="legalName" max={TenantFieldLengths.legalName} mt={0} />
              </Flex>
            </Box>
            <Box>
              <FormLabel tid="miEmpresa.fields.commercialName" />
              <Input {...register('commercialName')} maxLength={TenantFieldLengths.commercialName} />
              <CharCounter name="commercialName" max={TenantFieldLengths.commercialName} />
            </Box>
            <Box>
              <FormLabel tid="miEmpresa.fields.prefix" />
              <Input {...register('prefix')} maxLength={TenantFieldLengths.prefix} />
              <Flex justifyContent="space-between" alignItems="center" mt={1}>
                {errors.prefix ? (
                  <Text fontSize="xs" color={c.error}>{t(errors.prefix.message ?? '')}</Text>
                ) : <span />}
                <CharCounter name="prefix" max={TenantFieldLengths.prefix} mt={0} />
              </Flex>
            </Box>
            <Box>
              <FormLabel tid="miEmpresa.fields.taxAddress" />
              <Input {...register('taxAddress')} maxLength={TenantFieldLengths.taxAddress} />
              <CharCounter name="taxAddress" max={TenantFieldLengths.taxAddress} />
            </Box>
            <Box gridColumn={{ md: 'span 2' }}>
              <UbigeoSelect
                value={ubigeoCode ?? ''}
                onChange={(code) => setValue('ubigeoCode', code, { shouldValidate: true })}
              />
              {errors.ubigeoCode && <Text fontSize="xs" color={c.error} mt={1}>{t(errors.ubigeoCode.message ?? '')}</Text>}
            </Box>
          </Grid>
        </FormSection>

        <FormSection
          icon={LandPlot}
          titleTid="miEmpresa.sections.poligono"
          descriptionTid="miEmpresa.sections.poligonoDescription"
        >
          <PolygonEditor value={polygonValue} onChange={setPolygonValue} mapHeight="450px" />
          {polygonValue.vertices.length > 0 && polygonValue.vertices.length < 3 && (
            <Box mt={3} p={3} bg="#fff3cd" color="#856404" borderRadius="md" fontSize="xs">
              {t('miEmpresa.polygonIncompleteHint')}
            </Box>
          )}
        </FormSection>

        <Flex justifyContent="flex-end" mt={4} mb={8}>
          <Box
            as="button"
            type="submit"
            disabled={isSubmitting}
            display="flex"
            alignItems="center"
            gap={2}
            px={6}
            h="44px"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="700"
            bg="#b4532a"
            color="white"
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
            opacity={isSubmitting ? 0.6 : 1}
            _hover={{ bg: '#143326' }}
          >
            {isSubmitting && <Spinner size="sm" />}
            {t('miEmpresa.actions.saveIdentidadPoligono')}
          </Box>
        </Flex>

        <FormSection
          icon={MapPinned}
          titleTid="miEmpresa.sections.componentes"
          descriptionTid="miEmpresa.sections.componentesDescription"
        >
          {tenantId ? (
            <ComponentsEditor
              utmZone={polygonValue.utmZone}
              polygonGeoJson={polygonGeoJsonForPreview}
              components={editorComponents}
              availableTypes={availableTypes}
              onAdd={handleAddComponent}
              onRemove={handleRemoveComponent}
              mapHeight="450px"
            />
          ) : (
            <VStack><Spinner /></VStack>
          )}
        </FormSection>
      </form>
      </FormProvider>
    </Box>
  )
}

